import { useEffect, useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useAuth0 } from '../hooks/useAuth0'
import { useOnboardingSession } from '../hooks/useOnboardingSession'
import { useMutation } from 'convex/react'
import { api } from '../../convex/_generated/api'
import { Button } from '../components/common/Button'
import { Id } from '../../convex/_generated/dataModel'

/**
 * OAuth callback handler (FR-005, FR-006)
 * Completes onboarding after Auth0 redirect
 * Handles both web-based and voice onboarding flows
 */
export function OAuthCallback() {
  const navigate = useNavigate()
  const { user, isLoading, isAuthenticated } = useAuth0()
  const { session, clearSession } = useOnboardingSession()
  const [error, setError] = useState<string | null>(null)

  const completeOnboarding = useMutation(api.onboarding.completeOnboarding)
  const completeVoiceOnboarding = useMutation(api.onboarding.completeVoiceOnboarding)

  useEffect(() => {
    async function handleCallback() {
      if (isLoading) return

      if (!isAuthenticated || !user) {
        setError('Authentication failed')
        return
      }

      // Check for voice onboarding callId in localStorage
      const voiceOnboardingCallId = localStorage.getItem('voiceOnboardingCallId')

      // Check if user is coming from web onboarding (has session data)
      const hasWebOnboardingData = session.lovedOneName && session.lovedOnePhone

      // Detect timezone client-side (must happen in browser, not server)
      const detectedTimezone = (() => {
        try {
          return Intl.DateTimeFormat().resolvedOptions().timeZone
        } catch {
          return 'America/Los_Angeles' // Default to Pacific if detection fails
        }
      })()

      if (voiceOnboardingCallId) {
        // Voice onboarding flow - complete from call data
        console.log('Voice onboarding detected, completing from call data')
        try {
          await completeVoiceOnboarding({
            callId: voiceOnboardingCallId as Id<'calls'>,
            auth0Id: user.sub!,
            email: user.email!,
            timezone: detectedTimezone,
          })

          // Clear the callId from localStorage
          localStorage.removeItem('voiceOnboardingCallId')

          // Redirect to dashboard
          navigate('/dashboard')
        } catch (err) {
          console.error('Voice onboarding error:', err)
          localStorage.removeItem('voiceOnboardingCallId')
          setError(err instanceof Error ? err.message : 'Failed to complete onboarding')
        }
        return
      }

      if (!hasWebOnboardingData) {
        // User came from login page, not onboarding - just redirect to dashboard
        console.log('Existing user login detected, redirecting to dashboard')
        navigate('/dashboard')
        return
      }

      try {
        // Complete onboarding with session data (new user signup)
        await completeOnboarding({
          caregiver: {
            auth0Id: user.sub!,
            email: user.email!,
            notificationEmail: session.caregiverEmail,
            phoneNumber: session.caregiverPhone,
            interfaceLanguage: session.caregiverInterfaceLanguage || 'en',
            smsConsent: session.smsConsent || false,
            emailConsent: session.emailConsent || false,
          },
          lovedOne: {
            name: session.lovedOneName!,
            phoneNumber: session.lovedOnePhone!,
            email: session.lovedOneEmail,
            languagePreference: session.languagePreference || 'en',
            timezone: session.timezone || detectedTimezone,
          },
        })

        // Clear session after successful onboarding
        clearSession()

        // Redirect to dashboard (FR-041)
        navigate('/dashboard')
      } catch (err) {
        console.error('Onboarding error:', err)
        setError(err instanceof Error ? err.message : 'Failed to complete onboarding')
      }
    }

    handleCallback()
  }, [
    isLoading,
    isAuthenticated,
    user,
    session,
    completeOnboarding,
    completeVoiceOnboarding,
    clearSession,
    navigate,
  ])

  const containerStyles = {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '2rem',
    fontFamily: 'system-ui, -apple-system, sans-serif',
    backgroundColor: '#f8f9fa',
  }

  const contentStyles = {
    textAlign: 'center' as const,
    maxWidth: '500px',
  }

  if (error) {
    // Check if this is a duplicate account error
    const isDuplicateAccount = error.includes('already exists')

    if (isDuplicateAccount) {
      return (
        <div style={containerStyles}>
          <div style={contentStyles}>
            <img
              src="/error_logo.png"
              alt="Little Sister"
              style={{
                width: '120px',
                height: '120px',
                marginBottom: '1.5rem',
                objectFit: 'contain',
                display: 'block',
                marginLeft: 'auto',
                marginRight: 'auto',
              }}
            />
            <h2
              style={{ color: '#1a1a1a', marginBottom: '1rem', fontSize: '24px', fontWeight: 700 }}
            >
              Account Already Registered
            </h2>
            <p style={{ color: '#666', marginBottom: '2rem', fontSize: '16px', lineHeight: 1.5 }}>
              It looks like this email address is already registered with us. Try logging in
              instead.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <Button onClick={() => navigate('/login')} fullWidth>
                Go to Login
              </Button>
              <Link
                to="/"
                style={{
                  color: '#2563eb',
                  textDecoration: 'none',
                  fontSize: '14px',
                }}
              >
                Return to Home
              </Link>
            </div>
          </div>
        </div>
      )
    }

    // Generic error display
    return (
      <div style={containerStyles}>
        <div style={contentStyles}>
          <h2 style={{ color: '#dc3545', marginBottom: '1rem' }}>Onboarding Error</h2>
          <p style={{ color: '#666', marginBottom: '2rem' }}>{error}</p>
          <Button onClick={() => navigate('/')}>Return to Home</Button>
        </div>
      </div>
    )
  }

  return (
    <div style={containerStyles}>
      <div style={contentStyles}>
        <div
          style={{
            width: '48px',
            height: '48px',
            border: '4px solid #e0e0e0',
            borderTopColor: '#007AFF',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 1rem',
          }}
        />
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
        <p style={{ fontSize: '18px', color: '#666' }}>
          {isLoading ? 'Authenticating...' : 'Completing your setup...'}
        </p>
      </div>
    </div>
  )
}
